import * as React from 'react';
import { Box, Grid, styled } from '@mui/material';
import { FormikUtils, NumberUtils } from '../utils';
import { CerebroInput } from './CerebroInput';
import { fontFamily, weights } from '../context/CerebroThemeProvider';
import { useDebounce } from '../hooks';
import { CerebroIconButton } from './CerebroIconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalculator } from '@fortawesome/pro-duotone-svg-icons';
import { CerebroDrawer } from './CerebroDrawer';
import { CerebroPrimaryButton } from './CerebroPrimaryButton';
import { Field, Formik, FormikErrors } from 'formik';
import { CerebroHeading } from './CerebroHeading';
import { CerebroForm } from './CerebroForm';
import { CerebroTextarea } from './CerebroTextarea';
import { CerebroText } from './CerebroText';
import { MaxCurrencyLength } from '../utils/platform';

type CerebroInputProps = React.ComponentPropsWithoutRef<typeof CerebroInput>;

type CerebroMaskedInputProps = Omit<CerebroInputProps, 'value'> & {
	value: string | number | undefined;
	formatAs?: NumberUtils.FormatStyles;
	allowDecimals?: boolean;
	minDecimalPlaces?: number;
	maxDecimalPlaces?: number;
	calculator?: boolean;
};

const Input: React.FC<CerebroMaskedInputProps> = ({
	formatAs = 'decimal',

	minDecimalPlaces,
	maxDecimalPlaces,
	allowDecimals,

	onChange,
	onBlur: handleBlur,
	onFocus: handleFocus,

	...props
}) => {
	const format = React.useCallback(
		(value: number | string | undefined, allowHyphen: boolean) => {
			if (!value) return '';
			if (value === '-' && allowHyphen) return value;
			const updatedAllowDecimals = allowDecimals ?? false;

			let options: NumberUtils.FormatOptions = {
				minimumFractionDigits: updatedAllowDecimals ? minDecimalPlaces : 0,
				maximumFractionDigits: updatedAllowDecimals ? maxDecimalPlaces : 0,
			};

			return typeof value === 'number'
				? NumberUtils.FormatNumber(value, formatAs, options) ?? ''
				: NumberUtils.FormatStringAsNumber(value, formatAs, options) ?? '';
		},
		[formatAs, allowDecimals, minDecimalPlaces, maxDecimalPlaces]
	);

	const handleSubmit = React.useCallback((values: CalculatorValues) => {
		const equation = (values.equation ?? '').replace(/[^-()\d/*+.]/g, '');
		const equationResult = eval(equation);

		setInputValue(NumberUtils.FormatCurrency(equationResult.toString()));
		setCalculator(false);
	}, []);

	const [inputValue, setInputValue] = React.useState(props.value);
	const [calculator, setCalculator] = React.useState(false);
	const debouncedValue = useDebounce(inputValue, 1000);

	React.useEffect(() => {
		onChange?.({
			target: {
				id: props.id,
				name: props.name,
				value: format(debouncedValue, true),
			},
		} as React.ChangeEvent<HTMLInputElement>);
	}, [debouncedValue, props.id, props.name, onChange, format, calculator]);

	const onFocus: React.FocusEventHandler<HTMLInputElement> = event => {
		handleFocus?.(event);
		event.target.select();
	};

	const onBlur: React.FocusEventHandler<HTMLInputElement> = event => {
		event.target.value = format(event.target.value, false);

		onChange?.(event);
		handleBlur?.(event);
	};

	if (props.calculator) {
		return (
			<>
				<div style={{ position: 'relative' }}>
					<CerebroInput onFocus={onFocus} onChange={onChange} onBlur={onBlur} {...props} />
					<div style={{ position: 'absolute', right: '5px', top: '17px' }}>
						<CerebroIconButton onClick={() => setCalculator(true)} title="Click to view notes">
							<FontAwesomeIcon icon={faCalculator} size="xs" />
						</CerebroIconButton>
					</div>
				</div>

				<CerebroDrawer
					open={calculator}
					onClose={() => setCalculator(false)}
					PaperProps={{ sx: { width: '500px' } }}
				>
					<Box sx={{ maxWidth: { xs: 'auto', md: 400 } }}>
						<CerebroHeading>Input Calculator</CerebroHeading>
						<br />
						<Formik
							initialValues={{
								equation: '',
							}}
							validate={validateForm}
							onSubmit={(values: CalculatorValues) => handleSubmit(values)}
						>
							{({ isSubmitting, handleSubmit, values, errors, touched }) => {
								return (
									<CerebroForm flatIndicator onSubmit={handleSubmit}>
										<CerebroText gutterBottom>
											Use this calculator to add (+), subtract (-), multiply (*) or divide (/)
											multiple values. Operators () are also usable in your formula. When you
											save, the calculated value will be saved to the main input form.
										</CerebroText>
										<Box sx={{ marginBottom: 2 }}>
											<Field
												formatas="currency"
												as={CerebroTextarea}
												maxLength={MaxCurrencyLength}
												id="equation"
												name="equation"
												disabled={isSubmitting}
												value={values.equation}
												placeholder="1+2+3"
												rows="5"
												cols="50"
												error={FormikUtils.IsInvalid('equation', errors, touched)}
											/>
											{FormikUtils.IsInvalid('equation', errors, touched) && (
												<CerebroText variant="error">{errors.equation}</CerebroText>
											)}
											<CerebroText variant="hint">
												Enter the values as an equation for this input.
											</CerebroText>
										</Box>

										<Grid container justifyContent="space-between" sx={{ marginTop: 5 }}>
											<Grid item>
												<CerebroPrimaryButton loading={isSubmitting} type="submit">
													Save
												</CerebroPrimaryButton>
											</Grid>
										</Grid>
									</CerebroForm>
								);
							}}
						</Formik>
					</Box>
				</CerebroDrawer>
			</>
		);
	}

	return <CerebroInput onFocus={onFocus} onChange={onChange} onBlur={onBlur} {...props} />;
};

export const CerebroMaskedInput = styled(Input, { shouldForwardProp: prop => prop !== 'error' })(
	({ error, disabled, readOnly, theme }) => ({
		borderWidth: 2,
		borderStyle: 'solid',
		borderRadius: theme.shape.borderRadius,
		borderColor: error
			? theme.palette.error.main
			: disabled || readOnly
			? theme.palette.gray.light
			: theme.palette.gray.medium,

		color: disabled ? theme.palette.gray.light : theme.palette.gray.dark,
		cursor: disabled ? 'not-allowed' : 'initial',

		fontFamily,
		fontSize: 12,
		fontWeight: disabled ? weights.medium : weights.semibold,

		lineHeight: '14px',
		marginBottom: theme.spacing(1),
		marginTop: theme.spacing(1.5),
		padding: '14px 12px',

		outline: 'none',
		width: '100%',

		'&:focus:not(:read-only)': {
			borderColor: error ? theme.palette.error.light : theme.palette.primary.main,
		},

		'&::placeholder': {
			color: theme.palette.gray.medium,
		},
	})
);

interface CalculatorValues {
	equation?: string;
}

type CalculatorValueErrors = FormikErrors<CalculatorValues>;

function validateForm(values: CalculatorValues) {
	const errors: CalculatorValueErrors = {};

	try {
		const equation = (values.equation ?? '').replace(/[^-()\d/*+.]/g, '');
		eval(equation);
	} catch (e) {
		errors.equation = 'Equation is not valid.';
	}

	return errors;
}
